/**
 * 行程規劃（Journey planning）寫入後回饋文案：成功播報、重讀失敗提示與重新讀取按鈕。
 * {title} 帶入使用者原文（不翻譯）；{day} 為第幾天的數字；{date} 為既有格式化的日期標籤。
 * dayChipEmptyLabel 的 {day} 為既有日期標題（可能含使用者自訂標題，不翻譯）。
 * 元件只保存 key／狀態，渲染時才依目前語系取文案，切換語系後不會停在舊文案。
 */
import { createFlatMessageLookup } from './flatMessages';

const P = 'travel.planningFeedback';

export const PLANNING_FEEDBACK_KEYS = Object.freeze({
  activityCreated: `${P}.activityCreated`,
  activityUpdated: `${P}.activityUpdated`,
  refreshed: `${P}.refreshed`,
  refreshFailed: `${P}.refreshFailed`,
  retryRefresh: `${P}.retryRefresh`,
  retryingRefresh: `${P}.retryingRefresh`,
  activityMovedUp: `${P}.activityMovedUp`,
  activityMovedDown: `${P}.activityMovedDown`,
  activityMovedToDay: `${P}.activityMovedToDay`,
  activityDeleted: `${P}.activityDeleted`,
  dayMoved: `${P}.dayMoved`,
  moveConfirm: `${P}.moveConfirm`,
  moveConfirmLabel: `${P}.moveConfirmLabel`,
  dayChipEmpty: `${P}.dayChipEmpty`,
  dayChipEmptyLabel: `${P}.dayChipEmptyLabel`,
});

export const planningFeedbackMessagesZhTW = {
  [`${P}.activityCreated`]: '已新增安排',
  [`${P}.activityUpdated`]: '已儲存安排',
  [`${P}.refreshed`]: '畫面已更新',
  [`${P}.refreshFailed`]: '變更已儲存，但畫面尚未更新。',
  [`${P}.retryRefresh`]: '重新載入畫面',
  [`${P}.retryingRefresh`]: '重新載入中…',
  [`${P}.activityMovedUp`]: '已上移「{title}」',
  [`${P}.activityMovedDown`]: '已下移「{title}」',
  [`${P}.activityMovedToDay`]: '已將「{title}」移到第 {day} 天',
  [`${P}.activityDeleted`]: '已刪除「{title}」',
  [`${P}.dayMoved`]: '已將 {date} 移到第 {day} 天',
  [`${P}.moveConfirm`]: '移動',
  [`${P}.moveConfirmLabel`]: '移動「{title}」到所選日期',
  [`${P}.dayChipEmpty`]: '尚無安排',
  [`${P}.dayChipEmptyLabel`]: '{day}，尚無安排',
};

export const planningFeedbackMessagesEnUS = {
  [`${P}.activityCreated`]: 'Plan added',
  [`${P}.activityUpdated`]: 'Plan saved',
  [`${P}.refreshed`]: 'View updated',
  [`${P}.refreshFailed`]: 'Your change was saved, but the view hasn’t updated yet.',
  [`${P}.retryRefresh`]: 'Reload view',
  [`${P}.retryingRefresh`]: 'Reloading…',
  [`${P}.activityMovedUp`]: 'Moved “{title}” up',
  [`${P}.activityMovedDown`]: 'Moved “{title}” down',
  [`${P}.activityMovedToDay`]: 'Moved “{title}” to day {day}',
  [`${P}.activityDeleted`]: 'Deleted “{title}”',
  [`${P}.dayMoved`]: 'Moved {date} to day {day}',
  [`${P}.moveConfirm`]: 'Move',
  [`${P}.moveConfirmLabel`]: 'Move “{title}” to the selected day',
  [`${P}.dayChipEmpty`]: 'Nothing yet',
  [`${P}.dayChipEmptyLabel`]: '{day}, nothing planned yet',
};

export const planningFeedbackMessagesZhCN = {
  [`${P}.activityCreated`]: '已新增安排',
  [`${P}.activityUpdated`]: '已保存安排',
  [`${P}.refreshed`]: '画面已更新',
  [`${P}.refreshFailed`]: '变更已保存，但画面尚未更新。',
  [`${P}.retryRefresh`]: '重新加载画面',
  [`${P}.retryingRefresh`]: '重新加载中…',
  [`${P}.activityMovedUp`]: '已上移「{title}」',
  [`${P}.activityMovedDown`]: '已下移「{title}」',
  [`${P}.activityMovedToDay`]: '已将「{title}」移到第 {day} 天',
  [`${P}.activityDeleted`]: '已删除「{title}」',
  [`${P}.dayMoved`]: '已将 {date} 移到第 {day} 天',
  [`${P}.moveConfirm`]: '移动',
  [`${P}.moveConfirmLabel`]: '移动「{title}」到所选日期',
  [`${P}.dayChipEmpty`]: '尚无安排',
  [`${P}.dayChipEmptyLabel`]: '{day}，尚无安排',
};

export const getPlanningFeedbackMessage = createFlatMessageLookup({
  zh_TW: planningFeedbackMessagesZhTW,
  en_US: planningFeedbackMessagesEnUS,
  zh_CN: planningFeedbackMessagesZhCN,
});
